import { LegalEntityAction, PhoneAction } from './constants';

export interface CampaignProps {
  id: string;
  campaign_id: string;
  description?: string;
}

export interface BrandProps {
  id: string;
  brand_id: string;
  reference_id?: string;
  brand_identity_status?: number;
  set_for_revet?: boolean;
}

export interface AccountProps {
  id: string;
  name?: string;
  brands?: BrandProps[];
  tcr_ready: boolean;
  tcr_brand?: BrandProps;
}

export interface ParentAccount {
  id: string;
  name: string;
  brands?: BrandProps[];
  tcr_ready: boolean;
  tcr_brand?: BrandProps;
}

export interface PhoneProps {
  id: string;
  number: string;
  campaign: CampaignProps;
  brand: BrandProps;
  account: AccountProps;
}

type SetPhonesPayloadType = {
  type: PhoneAction.SET_PHONES;
  phones: PhoneProps[];
  count: number;
  error?: string;
};

type SetCampaignsPayloadType = {
  type: PhoneAction.SET_CAMPAIGNS;
  campaigns: CampaignProps[];
};

type SetAccountsPayloadType = {
  type: PhoneAction.SET_ACCOUNTS;
  accounts: AccountProps[];
};

type SetLoadingPhonesPayloadType = {
  type: PhoneAction.SET_LOADING_PHONES;
  loading: boolean;
};

type SearchPhoneByNumberPayloadType = {
  type: PhoneAction.SEARCH_BY_NUMBER;
  phone: string;
};

type SearchPhoneByAccountPayloadType = {
  type: PhoneAction.SEARCH_BY_ACCOUNT;
  account: string;
};

type SearchPhoneByCampaignPayloadType = {
  type: PhoneAction.SEARCH_BY_CAMPAIGN;
  campaign: string;
};

type SetSelectedPhonesPayloadType = {
  type: PhoneAction.SET_SELECTED_PHONES;
  selectedPhones: string[];
};

type PaginatePhonesPayloadType = {
  type: PhoneAction.PAGINATE;
  page: number;
};

type SavingPayloadType = {
  type: PhoneAction.SET_ASSIGN_TO_CAMPAIGN | PhoneAction.SET_ASSIGN_TO_ACCOUNT;
  saving: boolean;
};

export type PhoneActionType =
  | SearchPhoneByNumberPayloadType
  | SetPhonesPayloadType
  | SetLoadingPhonesPayloadType
  | SearchPhoneByAccountPayloadType
  | SearchPhoneByCampaignPayloadType
  | SetCampaignsPayloadType
  | SetAccountsPayloadType
  | SetSelectedPhonesPayloadType
  | PaginatePhonesPayloadType
  | SavingPayloadType;

export type PhoneStateType = {
  search: {
    phone: string;
    account: string;
    campaign: string;
  };
  phones: PhoneProps[];
  campaigns: CampaignProps[];
  accounts: AccountProps[];
  loadingPhones: boolean;
  selectedPhones: string[];
  currentPage: number;
  totalCount: number;
  assigningToCampaign: boolean;
  assigningToAccount: boolean;
  error?: string;
};

export interface PhoneActions {
  setPhones: (phones: PhoneProps[], count: number, error?: string) => void;
  setLoadingPhones: (loading: boolean) => void;
  searchByNumber: (phone: string) => void;
  searchByAccount: (account: string) => void;
  searchByCampaign: (campaign: string) => void;
  setCampaigns: (campaigns: CampaignProps[]) => void;
  setAccounts: (accounts: AccountProps[]) => void;
  setSelectedPhones: (selectedPhones: string[]) => void;
  paginate: (page: number) => void;
  assignToCampaign: (phones: string[], campaign: string) => Promise<void>;
  assignToAccount: (phones: string[], account: string) => Promise<void>;
}

export type RequestApiPaginationResponseType<T> = {
  data: {
    count: number;
    next: string;
    previous: string;
    results: T[];
  };
};

export type RequestApiListResponseType<T> = {
  data: T[];
};

export type RequestCreateTcrBrandResponseType = {
  data: {
    error: boolean;
    brand_id?: string;
    message?: string;
  };
};

export interface LegalEntityProps {
  id: string;
  name: string;
  legal_name: string;
  ein: string;
  accounts: AccountProps[];
  brands: BrandProps[];
  email: string;
  phone_number: string;
  website: string;
  street: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
  company_type: string;
  vertical_type: string;
}

type SetLegalEntitiesPayloadType = {
  type: LegalEntityAction.SET_LEGAL_ENTITIES;
  legalEntities: LegalEntityProps[];
  count: number;
  error?: string;
};

type SetLegalEntityAccountsPayloadType = {
  type: LegalEntityAction.SET_ACCOUNTS;
  accounts: AccountProps[];
};

type SetLoadingLegalEntitiesPayloadType = {
  type: LegalEntityAction.SET_LOADING_LEGAL_ENTITIES;
  loading: boolean;
};

type SearchLegalEntityByNamePayloadType = {
  type: LegalEntityAction.SEARCH_BY_NAME;
  name: string;
};

type SearchLegalEntityByAccountPayloadType = {
  type: LegalEntityAction.SEARCH_BY_ACCOUNT;
  account: string;
};

type SetSelectedLegalEntityPayloadType = {
  type: LegalEntityAction.SET_SELECTED_LEGAL_ENTITIES;
  selectedLegalEntities: string[];
};

type PaginateLegalEntitiesPayloadType = {
  type: LegalEntityAction.PAGINATE;
  page: number;
};

export type LegalEntityActionType =
  | SearchLegalEntityByNamePayloadType
  | SetLegalEntitiesPayloadType
  | SetLoadingLegalEntitiesPayloadType
  | SearchLegalEntityByAccountPayloadType
  | SetLegalEntityAccountsPayloadType
  | SetSelectedLegalEntityPayloadType
  | PaginateLegalEntitiesPayloadType;

export type LegalEntityStateType = {
  search: {
    name: string;
    account: string;
  };
  legalEntities: LegalEntityProps[];
  accounts: AccountProps[];
  loadingLegalEntities: boolean;
  selectedLegalEntities: string[];
  currentPage: number;
  totalCount: number;
  error?: string;
};

export interface LegalEntityActions {
  setLegalEntities: (legalEntities: LegalEntityProps[], count: number, error?: string) => void;
  setLoadingLegalEntities: (loading: boolean) => void;
  searchByName: (name: string) => void;
  searchByAccount: (account: string) => void;
  setAccounts: (accounts: AccountProps[]) => void;
  setSelectedLegalEntities: (selectedLegalEntities: string[]) => void;
  paginate: (page: number) => void;
}

export const campaignTypes = [
  {
    name: 'Low volume',
    id: 'LOW_VOLUME',
  },
  {
    name: 'Medium (customer care)',
    id: 'CUSTOMER_CARE',
  },
  {
    name: 'High (Social)',
    id: 'SOCIAL',
  },
];

export type CampaignDetailType = {
  id: string;
  name: string;
};
